import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import MediaPicker from "@/components/MediaPicker";
import SectionRenderer from "@/components/sections/SectionRenderer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Loader2, Plus, ChevronUp, ChevronDown, Copy, Trash2, Pencil, Eye,
  Save, UploadCloud, RotateCcw, GripVertical,
} from "lucide-react";

const PAGES = [{ key: "home", label: "Homepage" }, { key: "shop", label: "Shop Page" }];

const SCHEMAS = {
  hero: { label: "Hero", fields: [
    { k: "eyebrow", t: "text" }, { k: "headline", t: "text" }, { k: "subheadline", t: "textarea" },
    { k: "primary_cta", t: "text" }, { k: "primary_cta_link", t: "text" },
    { k: "secondary_cta", t: "text" }, { k: "secondary_cta_link", t: "text" }, { k: "image", t: "media" }] },
  banner: { label: "Banner", fields: [
    { k: "eyebrow", t: "text" }, { k: "heading", t: "text" }, { k: "text", t: "textarea" },
    { k: "image", t: "media" }, { k: "cta", t: "text" }, { k: "cta_link", t: "text" },
    { k: "style", t: "select", opts: ["teal", "navy", "dark", "light"] }] },
  promo: { label: "Promotional", fields: [
    { k: "eyebrow", t: "text" }, { k: "badge", t: "text" }, { k: "heading", t: "text" },
    { k: "product_slug", t: "text", help: "Optional: product slug to pull image & price" },
    { k: "text", t: "textarea" }, { k: "image", t: "media" }, { k: "cta", t: "text" }, { k: "cta_link", t: "text" }] },
  rich_points: { label: "Text + Points", fields: [
    { k: "eyebrow", t: "text" }, { k: "title", t: "text" },
    { k: "variant", t: "select", opts: ["problem", "solves"] }, { k: "points", t: "list-text" }] },
  feature_grid: { label: "Feature Grid", fields: [
    { k: "eyebrow", t: "text" }, { k: "title", t: "text" }, { k: "subtitle", t: "text" },
    { k: "items", t: "list-obj", sub: ["title", "desc"] }] },
  steps: { label: "Steps / How it works", fields: [
    { k: "eyebrow", t: "text" }, { k: "title", t: "text" },
    { k: "steps", t: "list-obj", sub: ["title", "desc"] }] },
  tags: { label: "Tag Chips", fields: [
    { k: "eyebrow", t: "text" }, { k: "title", t: "text" }, { k: "points", t: "list-text" }] },
  faq: { label: "FAQ", fields: [
    { k: "eyebrow", t: "text" }, { k: "title", t: "text" },
    { k: "items", t: "list-obj", sub: ["q", "a"] }] },
  founder: { label: "Founder", fields: [
    { k: "eyebrow", t: "text" }, { k: "title", t: "text" }, { k: "name", t: "text" },
    { k: "role", t: "text" }, { k: "bio", t: "textarea" }, { k: "image", t: "media" }] },
  cta: { label: "Call to Action", fields: [
    { k: "title", t: "text" }, { k: "subtitle", t: "text" }, { k: "cta", t: "text" }, { k: "cta_link", t: "text" }] },
  product_grid: { label: "Product Grid", fields: [
    { k: "eyebrow", t: "text" }, { k: "title", t: "text" },
    { k: "source", t: "select", opts: ["featured", "all", "by-slugs"] },
    { k: "slugs", t: "text", help: "Comma-separated slugs (when source = by-slugs)" },
    { k: "limit", t: "number" }, { k: "cta_link", t: "text" }] },
};

const label = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const uid = () => Math.random().toString(36).slice(2, 10);

function FieldEditor({ field, value, onChange }) {
  const { k, t } = field;
  if (t === "textarea") return <Textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${k}`} />;
  if (t === "number") return <Input type="number" value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))} data-testid={`field-${k}`} />;
  if (t === "media") return <MediaPicker value={value} onChange={onChange} />;
  if (t === "select") return (
    <select className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm" value={value || field.opts[0]} onChange={(e) => onChange(e.target.value)} data-testid={`field-${k}`}>
      {field.opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (t === "list-text") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-2">
        {arr.map((item, i) => (
          <div key={i} className="flex gap-2">
            <Input value={item} onChange={(e) => { const n = [...arr]; n[i] = e.target.value; onChange(n); }} data-testid={`field-${k}-${i}`} />
            <Button type="button" variant="ghost" size="icon" onClick={() => onChange(arr.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-red-400" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...arr, ""])} data-testid={`field-${k}-add`}><Plus className="w-3 h-3 mr-1" /> Add</Button>
      </div>
    );
  }
  if (t === "list-obj") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-3">
        {arr.map((item, i) => (
          <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50">
            <div className="flex justify-between items-center"><span className="text-xs font-semibold text-slate-500">#{i + 1}</span>
              <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => onChange(arr.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button></div>
            {field.sub.map((sk) => (
              <Input key={sk} placeholder={label(sk)} value={item?.[sk] || ""} onChange={(e) => { const n = [...arr]; n[i] = { ...n[i], [sk]: e.target.value }; onChange(n); }} data-testid={`field-${k}-${i}-${sk}`} />
            ))}
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...arr, {}])} data-testid={`field-${k}-add`}><Plus className="w-3 h-3 mr-1" /> Add item</Button>
      </div>
    );
  }
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${k}`} />;
}

function SectionForm({ section, onChange }) {
  const schema = SCHEMAS[section.type];
  const setData = (k, v) => onChange({ ...section, data: { ...section.data, [k]: v } });
  return (
    <div className="space-y-4 p-4 border-t border-slate-100 bg-slate-50/60">
      {schema.fields.map((f) => (
        <div key={f.k}>
          <Label className="text-xs text-slate-600">{label(f.k)}</Label>
          {f.help && <p className="text-[11px] text-slate-400 mb-1">{f.help}</p>}
          <div className="mt-1"><FieldEditor field={f} value={section.data?.[f.k]} onChange={(v) => setData(f.k, v)} /></div>
        </div>
      ))}
    </div>
  );
}

export default function AdminPageBuilder() {
  const [page, setPage] = useState("home");
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [hasDraftChanges, setHasDraftChanges] = useState(false);
  const [editing, setEditing] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = async (p) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/page-builder/${p}`);
      setSections(data.draft || []);
      setHasDraftChanges(data.has_draft_changes);
      setDirty(false);
    } catch { toast.error("Failed to load layout"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(page); }, [page]);

  const update = (next) => { setSections(next); setDirty(true); };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const n = [...sections];
    [n[i], n[j]] = [n[j], n[i]];
    update(n);
  };
  const duplicate = (i) => {
    const copy = { ...sections[i], id: uid(), data: JSON.parse(JSON.stringify(sections[i].data || {})) };
    const n = [...sections];
    n.splice(i + 1, 0, copy);
    update(n);
    toast.success("Section duplicated");
  };
  const remove = (i) => { update(sections.filter((_, j) => j !== i)); if (editing === sections[i].id) setEditing(null); };
  const toggle = (i) => { const n = [...sections]; n[i] = { ...n[i], enabled: !n[i].enabled }; update(n); };
  const editSection = (id, next) => update(sections.map((s) => (s.id === id ? next : s)));
  const addSection = (type) => {
    update([...sections, { id: uid(), type, enabled: true, data: {} }]);
    setAddOpen(false);
    setEditing(null);
    setTimeout(() => setEditing(sections.length ? null : null), 0);
    toast.success(`${SCHEMAS[type].label} added`);
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/page-builder/${page}`, { sections });
      setSections(data.draft || []);
      setHasDraftChanges(data.has_draft_changes);
      setDirty(false);
      toast.success("Draft saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const publish = async () => {
    setSaving(true);
    try {
      if (dirty) await api.put(`/admin/page-builder/${page}`, { sections });
      const { data } = await api.post(`/admin/page-builder/${page}/publish`);
      setSections(data.draft || []);
      setHasDraftChanges(false);
      setDirty(false);
      toast.success("Published — live on the storefront");
    } catch { toast.error("Publish failed"); }
    finally { setSaving(false); }
  };
  const revert = async () => {
    if (!window.confirm("Discard draft changes and reset to the published version?")) return;
    try { await api.post(`/admin/page-builder/${page}/revert`); load(page); toast.success("Reverted to published"); }
    catch { toast.error("Revert failed"); }
  };
  const openPreview = async () => {
    if (dirty) await saveDraft();
    try {
      const { data } = await api.get(`/admin/page-builder/${page}/preview`);
      setPreview((data.sections || []).filter((s) => s.enabled));
    } catch { toast.error("Preview failed"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="text-2xl font-bold text-white">Visual Page Builder</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={openPreview} data-testid="pb-preview"><Eye className="w-4 h-4 mr-1" /> Preview</Button>
          <Button variant="outline" size="sm" onClick={revert} disabled={!hasDraftChanges && !dirty} data-testid="pb-revert"><RotateCcw className="w-4 h-4 mr-1" /> Discard</Button>
          <Button size="sm" className="bg-slate-700 hover:bg-slate-600 text-white" onClick={saveDraft} disabled={saving || !dirty} data-testid="pb-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save draft
          </Button>
          <Button size="sm" className="bg-teal hover:bg-teal-dark text-white" onClick={publish} disabled={saving} data-testid="pb-publish">
            <UploadCloud className="w-4 h-4 mr-1" /> Publish
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <Tabs value={page} onValueChange={setPage}>
          <TabsList className="bg-slate-800">
            {PAGES.map((p) => <TabsTrigger key={p.key} value={p.key} data-testid={`pb-page-${p.key}`}>{p.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        {(dirty || hasDraftChanges) && <span className="text-xs text-amber-400" data-testid="pb-dirty">Unpublished changes</span>}
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-teal mx-auto" /></div>
      ) : (
        <div className="space-y-3" data-testid="pb-section-list">
          {sections.map((s, i) => (
            <div key={s.id} className={`bg-white rounded-xl border ${s.enabled ? "border-slate-200" : "border-dashed border-slate-300 opacity-70"}`} data-testid={`pb-section-${i}`}>
              <div className="flex items-center gap-2 p-3">
                <GripVertical className="w-4 h-4 text-slate-300 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-navy text-sm">{SCHEMAS[s.type]?.label || s.type}</div>
                  <div className="text-xs text-slate-400 truncate">{s.data?.headline || s.data?.title || s.data?.heading || s.data?.name || "—"}</div>
                </div>
                <Switch checked={s.enabled} onCheckedChange={() => toggle(i)} data-testid={`pb-toggle-${i}`} />
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`pb-up-${i}`}><ChevronUp className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(i, 1)} disabled={i === sections.length - 1} data-testid={`pb-down-${i}`}><ChevronDown className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => duplicate(i)} data-testid={`pb-dup-${i}`}><Copy className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(editing === s.id ? null : s.id)} data-testid={`pb-edit-${i}`}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(i)} data-testid={`pb-del-${i}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </div>
              {editing === s.id && <SectionForm section={s} onChange={(next) => editSection(s.id, next)} />}
            </div>
          ))}
          {!sections.length && <p className="text-center text-slate-400 py-10 text-sm">No sections yet. Add your first section below.</p>}
          <Button variant="outline" className="w-full border-dashed" onClick={() => setAddOpen(true)} data-testid="pb-add-section"><Plus className="w-4 h-4 mr-1" /> Add section</Button>
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add a section</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(SCHEMAS).map(([type, sc]) => (
              <Button key={type} variant="outline" className="justify-start" onClick={() => addSection(type)} data-testid={`pb-add-${type}`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" /> {sc.label}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={preview !== null} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="p-4 border-b sticky top-0 bg-white z-10"><DialogTitle>Preview — {PAGES.find((p) => p.key === page)?.label} (draft)</DialogTitle></DialogHeader>
          <div className="bg-background" data-testid="pb-preview-content">
            <SectionRenderer sections={preview || []} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
